if (!customElements.get('tab-list')) {
  customElements.define(
    'tab-list',
    class TabList extends HTMLUListElement {
      constructor() {
        super();

        this.controls.forEach((button) => button.addEventListener('click', this.handleButtonClick.bind(this)));
      }

      get controls() {
        return this._controls = this._controls || Array.from(this.querySelectorAll('[aria-controls]'));
      }

      handleButtonClick(event) {
        event.preventDefault();

        this.controls.forEach((button) => {
          button.setAttribute('aria-expanded', 'false');

          const panel = document.getElementById(button.getAttribute('aria-controls'));
          panel?.removeAttribute('open');
        });

        const target = event.currentTarget;
        target.setAttribute('aria-expanded', 'true');

        const panel = document.getElementById(target.getAttribute('aria-controls'));
        panel?.setAttribute('open', '');
      }

      reset() {
        const firstControl = this.controls[0];
        firstControl.dispatchEvent(new Event('click'));
      }
    }, { extends: 'ul' }
  );
}

if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CartDrawer extends DrawerElement {
      constructor() {
        super();

        this.onPrepareBundledSectionsListener = this.onPrepareBundledSections.bind(this);
        this.onCartRefreshListener = this.onCartRefresh.bind(this);
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      get shouldAppendToBody() {
        return false;
      }

      get recentlyViewed() {
        return this.querySelector('recently-viewed');
      }

      get tabList() {
        return this.querySelector('[is="tab-list"]');
      }

      connectedCallback() {
        super.connectedCallback();

        document.addEventListener('cart:bundled-sections', this.onPrepareBundledSectionsListener);
        document.addEventListener('cart:refresh', this.onCartRefreshListener);
        if (this.recentlyViewed) {
          this.recentlyViewed.addEventListener('is-empty', this.onRecentlyViewedEmpty.bind(this));
        }
      }

      disconnectedCallback() {
        super.disconnectedCallback();

        document.removeEventListener('cart:bundled-sections', this.onPrepareBundledSectionsListener);
        document.removeEventListener('cart:refresh', this.onCartRefreshListener);
      }

      onPrepareBundledSections(event) {
        event.detail.sections.push(this.sectionId);
      }

      onRecentlyViewedEmpty() {
        this.recentlyViewed.innerHTML = `
        <div class="drawer__scrollable relative flex justify-center items-start grow shrink text-center">
          <div class="drawer__empty grid gap-5 md:gap-8">
            <h2 class="drawer__empty-text heading leading-none tracking-tight">${theme.strings.recentlyViewedEmpty}</h2>
          </div>
        </div>
        `;
      }

      async onCartRefresh(event) {
        const id = `MiniCart-${this.sectionId}`;
        if (document.getElementById(id) === null) return;

        const responseText = await (await fetch(`${theme.routes.root_url}?section_id=${this.sectionId}`)).text();
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

        document.getElementById(id).innerHTML = parsedHTML.getElementById(id).innerHTML;

        if (event.detail.open === true) {
          this.show();
        }
      }

      show(focusElement = null, animate = true) {
        super.show(focusElement, animate);

        if (this.tabList) {
          this.tabList.reset();

          if (this.open) {
            theme.a11y.trapFocus(this, this.focusElement);
          }
        }
      }
    }
  );
}

if (!customElements.get('cart-remove-button')) {
  customElements.define(
    'cart-remove-button',
    class CartRemoveButton extends HTMLAnchorElement {
      constructor() {
        super();

        this.addEventListener('click', (event) => {
          event.preventDefault();

          const cartItems = this.closest('cart-items');
          cartItems.updateQuantity(this.getAttribute('data-index'), 0);
        });
      }
    }, { extends: 'a' }
  );
}

if (!customElements.get('cart-items')) {
  customElements.define(
    'cart-items',
    class CartItems extends HTMLElement {
      cartUpdateUnsubscriber = undefined;

      constructor() {
        super();

        this.addEventListener('change', theme.utils.debounce(this.onChange.bind(this), 300));
        this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      onChange(event) {
        this.validateQuantity(event);
      }

      onCartUpdate(event) {
        if (event.cart.errors) {
          this.onCartError(event.cart.errors, event.target);
          return;
        }

        const sectionToRender = new DOMParser().parseFromString(event.cart.sections[this.sectionId], 'text/html');

        const miniCart = document.querySelector(`#MiniCart-${this.sectionId}`);
        if (miniCart) {
          const updatedElement = sectionToRender.querySelector(`#MiniCart-${this.sectionId}`);
          if (updatedElement) {
            miniCart.innerHTML = updatedElement.innerHTML;
          }
        }

        const mainCart = document.querySelector(`#MainCart-${this.sectionId}`);
        if (mainCart) {
          const updatedElement = sectionToRender.querySelector(`#MainCart-${this.sectionId}`);
          if (updatedElement) {
            mainCart.innerHTML = updatedElement.innerHTML;
          }
          else {
            mainCart.closest('.cart').classList.add('is-empty');
            mainCart.remove();
          }
        }

        const lineItem = document.getElementById(`CartItem-${event.line}`) || document.getElementById(`CartDrawer-Item-${event.line}`);
        if (lineItem && lineItem.querySelector(`[name="${event.name}"]`)) {
          theme.a11y.trapFocus(mainCart || miniCart, lineItem.querySelector(`[name="${event.name}"]`));
        }
        else if (event.cart.item_count === 0) {
          miniCart
            ? theme.a11y.trapFocus(miniCart, miniCart.querySelector('a'))
            : theme.a11y.trapFocus(document.querySelector('.empty-state'), document.querySelector('.empty-state__link'));
        }
        else {
          miniCart
            ? theme.a11y.trapFocus(miniCart, miniCart.querySelector('.horizontal-product__title'))
            : theme.a11y.trapFocus(mainCart, mainCart.querySelector('.cart__item-title'));
        }

        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: {
            cart: event.cart
          }
        }));
      }

      onCartError(errors, target) {
        if (target) {
          // this.updateQuantity(target.getAttribute('data-index'), target.defaultValue, document.activeElement.getAttribute('name'), target);
          this.disableLoading(target.getAttribute('data-index'));
          this.setValidity(target, errors);
          return;
        }
        else {
          window.location.href = theme.routes.cart_url;
        }

        alert(errors);
      }

      updateQuantity(line, quantity, name, target) {
        this.enableLoading(line);

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

        const body = JSON.stringify({
          id: line,
          quantity,
          sections: sectionsToBundle
        });

        fetch(`${theme.routes.cart_change_url}`, { ...theme.utils.fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cart: parsedState, target, line, name });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          });
      }

      enableLoading(line) {
        const loader = document.getElementById(`Loader-${this.sectionId}-${line}`);
        if (loader) loader.hidden = false;
      }

      disableLoading(line) {
        const loader = document.getElementById(`Loader-${this.sectionId}-${line}`);
        if (loader) loader.hidden = true;
      }

      setValidity(target, message) {
        target.setCustomValidity(message);
        target.reportValidity();
        target.value = target.defaultValue;
        target.select();
      }

      validateQuantity(event) {
        const target = event.target;
        const inputValue = parseInt(target.value);
        const index = target.getAttribute('data-index');
        let message = '';

        if (inputValue < parseInt(target.getAttribute('data-min'))) {
          message = theme.quickOrderListStrings.minError.replace('[min]', target.getAttribute('data-min'));
        }
        else if (inputValue > parseInt(target.max)) {
          message = theme.quickOrderListStrings.maxError.replace('[max]', target.max);
        }
        else if (inputValue % parseInt(target.step) !== 0) {
          message = theme.quickOrderListStrings.stepError.replace('[step]', target.step);
        }

        if (message) {
          this.setValidity(target, message);
        }
        else {
          target.setCustomValidity('');
          target.reportValidity();
          this.updateQuantity(index, inputValue, document.activeElement.getAttribute('name'), target);
        }
      }
    }
  );
}

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener('change', theme.utils.debounce(this.onChange.bind(this), 300));
      }

      onChange(event) {
        const body = JSON.stringify({ note: event.target.value });
        fetch(`${theme.routes.cart_update_url}`, { ...theme.utils.fetchConfig(), ...{ body } });
      }
    }
  );
}

if (!customElements.get('main-cart')) {
  customElements.define(
    'main-cart',
    class MainCart extends HTMLElement {
      constructor() {
        super();

        document.addEventListener('cart:bundled-sections', this.onPrepareBundledSections.bind(this));
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      onPrepareBundledSections(event) {
        event.detail.sections.push(this.sectionId);
      }
    }
  );
}

if (!customElements.get('country-province')) {
  customElements.define(
    'country-province',
    class CountryProvince extends HTMLElement {
      constructor() {
        super();

        this.provinceElement = this.querySelector('[name="address[province]"]');
        this.countryElement = this.querySelector('[name="address[country]"]');
        this.countryElement.addEventListener('change', this.handleCountryChange.bind(this));

        if (this.getAttribute('country') !== '') {
          this.countryElement.selectedIndex = Math.max(0, Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.getAttribute('data-country')));
          this.countryElement.dispatchEvent(new Event('change'));
        }
        else {
          this.handleCountryChange();
        }
      }

      handleCountryChange() {
        const option = this.countryElement.options[this.countryElement.selectedIndex], provinces = JSON.parse(option.getAttribute('data-provinces'));
        this.provinceElement.parentElement.hidden = provinces.length === 0;

        if (provinces.length === 0) {
          return;
        }

        this.provinceElement.innerHTML = '';

        provinces.forEach((data) => {
          const selected = data[1] === this.getAttribute('data-province');
          this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
        });
      }
    }
  );
}

if (!customElements.get('shipping-calculator')) {
  customElements.define(
    'shipping-calculator',
    class ShippingCalculator extends HTMLFormElement {
      constructor() {
        super();

        this.onSubmitHandler = this.onSubmit.bind(this);
      }

      connectedCallback() {
        this.submitButton = this.querySelector('[type="submit"]');
        this.resultsElement = this.lastElementChild;

        this.submitButton.addEventListener('click', this.onSubmitHandler);
      }

      disconnectedCallback() {
        this.submitButton.removeEventListener('click', this.onSubmitHandler);
      }

      onSubmit(event) {
        event.preventDefault();

        this.abortController?.abort();
        this.abortController = new AbortController();

        const zip = this.querySelector('[name="address[zip]"]').value,
          country = this.querySelector('[name="address[country]"]').value,
          province = this.querySelector('[name="address[province]"]').value;

        this.submitButton.setAttribute('aria-busy', 'true');

        const body = JSON.stringify({
          shipping_address: { zip, country, province }
        });
        let sectionUrl = `${theme.routes.cart_url}/shipping_rates.json`;

        // remove double `/` in case shop might have /en or language in URL
        sectionUrl = sectionUrl.replace('//', '/');

        fetch(sectionUrl, { ...theme.utils.fetchConfig('javascript'), ...{ body }, signal: this.abortController.signal })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.shipping_rates) {
              this.formatShippingRates(parsedState.shipping_rates);
            }
            else {
              this.formatError(parsedState);
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          })
          .finally(() => {
            this.resultsElement.hidden = false;
            this.submitButton.removeAttribute('aria-busy');
          });
      }

      formatError(errors) {
        const shippingRatesList = Object.keys(errors).map((errorKey) => {
          return `<li>${errors[errorKey]}</li>`;
        });
        this.resultsElement.innerHTML = `
          <div class="alert alert--error grid gap-2 text-sm leading-tight">
            <p>${theme.shippingCalculatorStrings.error}</p>
            <ul class="list-disc grid gap-2" role="list">${shippingRatesList.join('')}</ul>
          </div>
        `;
      }

      formatShippingRates(shippingRates) {
        const shippingRatesList = shippingRates.map(({ presentment_name, currency, price }) => {
          return `<li>${presentment_name}: ${currency} ${price}</li>`;
        });
        this.resultsElement.innerHTML = `
          <div class="alert alert--${shippingRates.length === 0 ? 'error' : 'success'} grid gap-2 text-sm leading-tight">
            <p>${shippingRates.length === 0 ? theme.shippingCalculatorStrings.notFound : shippingRates.length === 1 ? theme.shippingCalculatorStrings.oneResult : theme.shippingCalculatorStrings.multipleResults}</p>
            ${shippingRatesList === '' ? '' : `<ul class="list-disc grid gap-2" role="list">${shippingRatesList.join('')}</ul>`}
          </div>
        `;

      }
    }, { extends: 'form' }
  );
}

document.addEventListener('DOMContentLoaded', () => {
  const BACKEND_URL = "https://zzcustom-furniture-modeller.vercel.app/api/create_draft_order";
  let cartFetchTimeout = null;

  const isOutletItem = (item) => {
    try {
      if (item.product_title && String(item.product_title).toLowerCase().includes('outlet')) return true;
      if (item.title && String(item.title).toLowerCase().includes('outlet')) return true;
      if (item.url && String(item.url).toLowerCase().includes('outlet')) return true;
      const row = document.getElementById(`CartItem-${item.key}`);
      if (row && row.dataset.isOutlet === 'true') return true;
    } catch (e) {
      console.warn("Error in isOutletItem", e);
    }
    return false;
  };

  // Returns tier discount % based on total cart value in cents
  const getTierDiscount = (totalCents) => {
    if (totalCents >= 4500000) return 24;
    if (totalCents >= 4000000) return 22;
    if (totalCents >= 3500000) return 20;
    if (totalCents >= 3000000) return 18;
    if (totalCents >= 2500000) return 16;
    if (totalCents >= 2000000) return 14;
    if (totalCents >= 1500000) return 12;
    if (totalCents >= 1000000) return 10;
    if (totalCents >= 500000)  return 8;
    return 0;
  };

  // Calculates gross cart total in cents (used to determine tier discount)
  const getCartGrossTotal = (items) => {
    let total = 0;
    items.forEach(item => {
      if (isOutletItem(item)) {
        return;
      }
      if (item.properties && item.properties["Estimated Total"]) {
        let pStr = item.properties["Estimated Total"];
        let clean = pStr.replace(/[^0-9.,]/g, '');
        if (clean.includes(',') && clean.includes('.')) {
          if (clean.indexOf(',') > clean.indexOf('.')) clean = clean.replace(/\./g, '').replace(',', '.');
          else clean = clean.replace(/,/g, '');
        } else if (clean.includes(',')) clean = clean.replace(/,/g, '');
        const numeric = parseFloat(clean);
        if (!isNaN(numeric) && numeric > 0) {
          total += Math.round(numeric * 100) * item.quantity;
          return;
        }
      }
      total += item.original_price * item.quantity;
    });
    return total;
  };

  const getDiscountedPrice = (item, row, cartTierDisc) => {
    // 1. Configurator Logic — apply cart tier discount on top of Estimated Total
    if (item.properties && item.properties["Estimated Total"]) {
      let pStr = item.properties["Estimated Total"];
      let clean = pStr.replace(/[^0-9.,]/g, '');
      if (clean.includes(',') && clean.includes('.')) {
        if (clean.indexOf(',') > clean.indexOf('.')) clean = clean.replace(/\./g, '').replace(',', '.');
        else clean = clean.replace(/,/g, '');
      } else if (clean.includes(',')) clean = clean.replace(/,/g, '');
      const numeric = parseFloat(clean);
      if (!isNaN(numeric) && numeric > 0) {
        const grossCents = Math.round(numeric * 100);
        const priceCents = cartTierDisc > 0 ? Math.round(grossCents * (1 - cartTierDisc / 100)) : grossCents;
        return { priceCents, grossCents, isSpecial: true };
      }
    }

    // 2. Tiered Sofa Logic — discount % based on full cart total
    if (row && (row.dataset.tierEnabled === 'true' || parseFloat(row.dataset.individualDiscount) > 0)) {
      if (isOutletItem(item)) {
        return { priceCents: item.final_price, grossCents: item.original_price, isSpecial: false };
      }
      const grossCents = item.original_price;
      const ind = parseFloat(row.dataset.individualDiscount) || 0;
      const disc = ind > 0 ? ind : cartTierDisc;
      if (disc > 0) {
        return { priceCents: Math.round(grossCents * (1 - disc / 100)), grossCents, isSpecial: true };
      }
    }

    return { priceCents: item.final_price, grossCents: item.original_price, isSpecial: false };
  };

  const performCartPatch = async () => {
    try {
      const res = await fetch('/cart.js');
      const cartData = await res.json();
      if (!cartData || !cartData.items) return;

      const cartTierDisc = getTierDiscount(getCartGrossTotal(cartData.items));

        let grossTotalCents = 0;
        let discountedTotalCents = 0;
        let hasSpecial = false;

        cartData.items.forEach(item => {
          const row = document.getElementById(`CartItem-${item.key}`);
          const res = getDiscountedPrice(item, row, cartTierDisc);
          if (res.isSpecial) hasSpecial = true;

          grossTotalCents += res.grossCents * item.quantity;
          discountedTotalCents += res.priceCents * item.quantity;

          if (row) {
            // Show original price only — no strikethrough
            const displayCents = res.grossCents * item.quantity;
            const fOL = '$' + (displayCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 });
            const fmtLine = `${fOL} <span style="font-size:0.8em;opacity:0.7">USD</span>`;

            const nodes = row.querySelectorAll('.price, .price-item, .cart-item__price-value, .cart-item__totals .price-item, .product-price');
            nodes.forEach(n => {
              const isLine = n.closest('.cart-item__totals') || n.closest('td.text-right') || n.closest('td:last-child');
              if (isLine) {
                n.innerHTML = fmtLine;
                n.style.display = 'block';
              } else {
                n.style.display = 'none';
              }
            });
          }
        });

        if (hasSpecial) {
          const fmt = (cents) => '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 }) + ' <span style="font-size:0.8em;opacity:0.7">USD</span>';
          const hasDiscount = discountedTotalCents < grossTotalCents;
          const subtotalHTML = hasDiscount
            ? `<span style="display:block;text-align:right">${fmt(grossTotalCents)}<span style="font-size:0.75em;opacity:0.6;margin-left:6px">-${cartTierDisc}%</span></span><span style="display:block;text-align:right;color:#c00;font-weight:700">${fmt(discountedTotalCents)}</span>`
            : fmt(grossTotalCents);
          document.querySelectorAll('.totals__subtotal-value, .cart-drawer__footer .price, #main-cart-footer .price-item--last').forEach(el => {
            el.innerHTML = subtotalHTML;
          });
        }
    } catch (e) { console.error("[Cart Patch Error]", e); }
  };

  const onCartChange = () => {
    clearTimeout(cartFetchTimeout);
    cartFetchTimeout = setTimeout(performCartPatch, 400);
  };

  const obs = new MutationObserver(onCartChange);
  const cont = document.querySelector('cart-items') || document.body;
  obs.observe(cont, { subtree: true, childList: true });

  document.addEventListener('cart:updated', onCartChange);
  document.addEventListener('cart:refresh', onCartChange);
  onCartChange();

  // Unified Checkout Interceptor
  document.addEventListener('click', async (e) => {
    const checkoutBtn = e.target.closest('button[name="checkout"], input[name="checkout"], a[href="/checkout"]');
    if (!checkoutBtn) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    try {
      const res = await fetch('/cart.js');
      const cartData = await res.json();
      
      const cartTierDiscCheckout = getTierDiscount(getCartGrossTotal(cartData.items));

      let specialItemsPresent = false;
      const itemsToProcess = cartData.items.map(item => {
        const row = document.getElementById(`CartItem-${item.key}`);
        const res = getDiscountedPrice(item, row, cartTierDiscCheckout);
        if (res.isSpecial) specialItemsPresent = true;
        
        let newItem = { ...item, price: res.priceCents, final_price: res.priceCents };
        if (res.isSpecial) {
           if (!newItem.properties) newItem.properties = {};
           // Ensure it has the property that likely triggers price override in Vercel
           newItem.properties["Estimated Total"] = '$' + (res.priceCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 });
        }
        return newItem;
      });

      if (specialItemsPresent) {
        checkoutBtn.disabled = true;
        checkoutBtn.innerText = "Processing checkout...";
        try {
          // Extract hidden production codes from item properties
          const productionParts = [];
          itemsToProcess.forEach(item => {
            if (item.properties && item.properties["_Production Codes"]) {
              productionParts.push(item.properties["_Production Codes"]);
              delete item.properties["_Production Codes"];
            }
          });
          const productionNotes = productionParts.join("\n");
          const noteInput = document.querySelector('cart-note textarea, [name="note"], .cart__note textarea');
          const customerNote = (noteInput ? noteInput.value : cartData.note) || "";
          const combinedNote = [customerNote, productionNotes].filter(n => n).join("\n---\n");
          const drRes = await fetch(BACKEND_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: itemsToProcess, note: combinedNote })
          });
          const drData = await drRes.json();
          if (drData.checkoutUrl) {
              window.location.href = drData.checkoutUrl;
          } else {
              throw new Error("No URL returned");
          }
        } catch (postErr) {
          console.error("Draft Order API error", postErr);
          window.location.href = '/checkout';
        }
      } else {
        const form = checkoutBtn.closest('form');
        if (form) {
          const input = document.createElement('input');
          input.type = 'hidden'; input.name = 'checkout'; input.value = 'Checkout';
          form.appendChild(input);
          form.submit();
        } else { window.location.href = '/checkout'; }
      }
    } catch (err) { window.location.href = '/checkout'; }
  }, true);
});
